import React, { useState } from "react";
import { CheckCircle2, XCircle, AlertTriangle, Download } from "lucide-react";
import { UploadIngredientBulkResponse } from "../../../../services/ingredients";

interface Props {
  result: UploadIngredientBulkResponse;
  /* When provided, an inline "Download error file" button is shown on any
     failure state (header mismatch or per-row errors). Wired by the parent
     to re-post the picked sheet with ?download_errors=true and stream the
     annotated Excel back to the browser. */
  onDownloadErrors?: () => Promise<void>;
}

// Small pill button + spinner used for the annotated-Excel download so
// the user gets in-place feedback while the second POST is in flight.
const DownloadErrorButton: React.FC<{ onClick: () => Promise<void> }> = ({
  onClick,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleClick = async () => {
    if (downloading) return;
    setDownloading(true);
    setError(null);
    try {
      await onClick();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to download the error file. Please try again.",
      );
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={downloading}
        className="self-start inline-flex items-center gap-1.5 rounded-md border border-[#0DB5B5] px-3 py-1.5 text-xs 3xl:text-sm font-inter text-[#0DB5B5] hover:bg-[#EBF7F4] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {downloading ? (
          <div className="w-3.5 h-3.5 border-2 border-[#0DB5B5] border-t-transparent rounded-full animate-spin" />
        ) : (
          <Download className="w-3.5 h-3.5" />
        )}
        {downloading ? "Downloading..." : "Download error file"}
      </button>
      {error && (
        <p className="text-xs 3xl:text-sm text-red-500 font-inter">{error}</p>
      )}
    </div>
  );
};

// Renders the API response in a human-readable form so the user can act on
// it without having to parse a toast. Handles all three shapes the backend
// returns: clean success, partial success (`error_rows` populated), and
// column-mismatch failure (`header_mismatches` populated).
const BulkUploadResultView: React.FC<Props> = ({ result, onDownloadErrors }) => {
  const isSuccess = result.success === "1" || result.success === "true";
  const headerMismatches = result.header_mismatches ?? [];
  const errorRows = result.error_rows ?? [];
  const addedItems = result.added_items ?? [];
  const updatedItems = result.updated_items ?? [];

  // Failure due to column mismatch — show the diff so the user can rename
  // the columns in their sheet and re-upload.
  if (!isSuccess) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-2 rounded-md bg-[#FEE2E2] px-3 py-2">
          <XCircle className="w-4 h-4 text-[#DC2626] mt-0.5 flex-shrink-0" />
          <p className="text-sm 3xl:text-base font-inter text-[#7F1D1D]">
            {result.message || "Upload failed"}
          </p>
        </div>

        {headerMismatches.length > 0 && (
          <div>
            <h4 className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F] mb-1">
              Column mismatches ({headerMismatches.length})
            </h4>
            <div className="border border-[#E5E7EB] rounded-md overflow-hidden max-h-[40vh] overflow-y-auto">
              <table className="w-full text-xs 3xl:text-sm font-inter">
                <thead className="bg-[#F9FAFB] text-[#6B7280] sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Column</th>
                    <th className="text-left px-3 py-2 font-medium">Expected</th>
                    <th className="text-left px-3 py-2 font-medium">Found</th>
                  </tr>
                </thead>
                <tbody>
                  {headerMismatches.map((m, i) => (
                    <tr key={i} className="border-t border-[#E5E7EB]">
                      <td className="px-3 py-1.5 text-[#1F1F1F]">{m.column}</td>
                      <td className="px-3 py-1.5 text-[#059669]">{m.expected}</td>
                      <td className="px-3 py-1.5 text-[#DC2626]">{m.found}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs 3xl:text-sm font-inter text-[#6B7280] mt-2">
              Fix these column names in your Excel file and upload it again.
            </p>
          </div>
        )}

        {onDownloadErrors && (
          <DownloadErrorButton onClick={onDownloadErrors} />
        )}
      </div>
    );
  }

  const added = result.added_count ?? 0;
  const updated = result.updated_count ?? 0;
  const skipped = result.skipped_count ?? 0;
  const errored = result.error_count ?? 0;
  const hasErrors = errored > 0 || errorRows.length > 0;

  // Success — banner turns amber when partial (some rows failed), green
  // otherwise. Counts + per-item lists give the user an audit trail.
  return (
    <div className="flex flex-col gap-3">
      <div
        className={`flex items-start gap-2 rounded-md px-3 py-2 ${
          hasErrors ? "bg-[#FEF3C7]" : "bg-[#EBF7F4]"
        }`}
      >
        {hasErrors ? (
          <AlertTriangle className="w-4 h-4 text-[#D97706] mt-0.5 flex-shrink-0" />
        ) : (
          <CheckCircle2 className="w-4 h-4 text-[#0DB5B5] mt-0.5 flex-shrink-0" />
        )}
        <p
          className={`text-sm 3xl:text-base font-inter ${
            hasErrors ? "text-[#78350F]" : "text-[#065F46]"
          }`}
        >
          {result.message || "Data uploaded successfully"}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile label="Added" count={added} tone="teal" />
        <StatTile label="Updated" count={updated} tone="blue" />
        <StatTile label="Skipped" count={skipped} tone="gray" />
        <StatTile label="Failed" count={errored} tone="red" />
      </div>

      {addedItems.length > 0 && (
        <ItemList label="Added items" items={addedItems} />
      )}
      {updatedItems.length > 0 && (
        <ItemList label="Updated items" items={updatedItems} />
      )}

      {errorRows.length > 0 && (
        <div>
          <h4 className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F] mb-1">
            Failed rows ({errorRows.length})
          </h4>
          <div className="border border-[#FCA5A5] rounded-md overflow-hidden max-h-[40vh] overflow-y-auto">
            <table className="w-full text-xs 3xl:text-sm font-inter">
              <thead className="bg-[#FEE2E2] text-[#7F1D1D] sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Sheet</th>
                  <th className="text-left px-3 py-2 font-medium">Row</th>
                  <th className="text-left px-3 py-2 font-medium">Item</th>
                  <th className="text-left px-3 py-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {errorRows.map((e, i) => (
                  <tr key={i} className="border-t border-[#FCA5A5]">
                    <td className="px-3 py-1.5 text-[#1F1F1F]">{e.sheet}</td>
                    <td className="px-3 py-1.5 text-[#1F1F1F]">
                      {e.row_number}
                    </td>
                    <td className="px-3 py-1.5 text-[#1F1F1F]">
                      {e.item_name}
                    </td>
                    <td className="px-3 py-1.5 text-[#7F1D1D]">{e.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hasErrors && onDownloadErrors && (
        <DownloadErrorButton onClick={onDownloadErrors} />
      )}
    </div>
  );
};

// Small labelled count tile. `count` renders in a colour keyed off `tone`
// so the visual weight matches the semantic (added = teal, failed = red).
const STAT_TONE: Record<string, { bg: string; fg: string }> = {
  teal: { bg: "bg-[#EBF7F4]", fg: "text-[#0DB5B5]" },
  blue: { bg: "bg-[#E0F2FE]", fg: "text-[#0369A1]" },
  gray: { bg: "bg-[#F3F4F6]", fg: "text-[#6B7280]" },
  red: { bg: "bg-[#FEE2E2]", fg: "text-[#DC2626]" },
};

const StatTile: React.FC<{
  label: string;
  count: number;
  tone: keyof typeof STAT_TONE;
}> = ({ label, count, tone }) => {
  const { bg, fg } = STAT_TONE[tone];
  return (
    <div className={`rounded-md px-3 py-2 ${bg}`}>
      <div className={`text-lg 3xl:text-xl font-semibold font-inter ${fg}`}>
        {count}
      </div>
      <div className="text-xs 3xl:text-sm font-inter text-[#6B7280]">
        {label}
      </div>
    </div>
  );
};

// Wraps a list of item names as small pills. Overflows scroll vertically
// so the modal footer stays visible on very large uploads.
const ItemList: React.FC<{ label: string; items: string[] }> = ({
  label,
  items,
}) => (
  <div>
    <h4 className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F] mb-1">
      {label} ({items.length})
    </h4>
    <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
      {items.map((name, i) => (
        <span
          key={`${name}-${i}`}
          className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#F3F4F6] text-xs 3xl:text-sm font-inter text-[#1F1F1F]"
        >
          {name}
        </span>
      ))}
    </div>
  </div>
);

export default BulkUploadResultView;
